/**
 * Full-width app header bar.
 *
 * Sits flush against the top of the viewport (edge-to-edge), with content
 * left-aligned and a thin bottom border to separate the header from the
 * page beneath. Reusable across:
 *   - Auth screens (login, signup, magic-link consume)
 *   - Onboarding flow (value pitch, intake, plan preview, paywall)
 *   - Any other unauthenticated / pre-app surface
 *
 * Right-side actions are pluggable: by default we render the language
 * picker + a "Help" link; pass ``rightSlot`` to override (e.g. to drop the
 * Help link on a particular screen, or add a "Sign out" button later).
 */
import React from 'react';
import { Image, Linking, Platform, TouchableOpacity, useWindowDimensions, View } from 'react-native';
import { useRouter } from 'expo-router';

import { Text } from '@/components/ui/text';
import { LanguageSelector } from '../common/LanguageSelector';
import { useTranslation } from '@/hooks/useTranslation';
import { AuthContext, type AuthPhase } from '../../context/AuthContext';
import { pathForPhase } from '../../navigation/phaseRedirect';

const COLORS = {
  background: '#FFFFFF',
  border: '#E5E7EB',
  text: { primary: '#111827', secondary: '#4B5563' },
};

const landingBase = (process.env.EXPO_PUBLIC_LANDING_URL || 'https://mentorcoaching.ai').replace(/\/$/, '');
// The landing site has no /help page (it 404'd); the FAQ section on the
// localized home page is the closest real help surface.
export function helpUrlFor(language: string | undefined): string {
  const locale = language === 'tr' ? 'tr' : 'en';
  return `${landingBase}/${locale}#faq`;
}

// Signed-in users stay in the app: the logo goes to the screen their phase
// allows (dashboard, or the current gated step such as plan selection)
// instead of opening the marketing site in a new tab mid-checkout.
export function logoTargetFor(phase: AuthPhase | undefined): { inApp: string } | { external: string } {
  if (!phase || phase === 'loading' || phase === 'unauthenticated') {
    return { external: landingBase };
  }
  return { inApp: pathForPhase(phase) };
}

// Pulled from the same source the native app + web favicon use, so we
// can't drift between brand surfaces by accident.
const LOGO_SRC = require('../../../assets/icon-web.png');

interface Props {
  /** Override the right-side actions cluster. Defaults to lang + Help. */
  rightSlot?: React.ReactNode;
  /** Hide the bottom border (useful when stacking the bar onto a coloured hero). */
  hideBorder?: boolean;
}

export default function AppHeaderBar({ rightSlot, hideBorder }: Props) {
  const { t, language } = useTranslation();
  const router = useRouter();
  const auth = React.useContext(AuthContext);
  const { width } = useWindowDimensions();
  const compact = width < 420;
  const veryCompact = width < 350;

  return (
    <View
      style={{
        // Full-width, edge-to-edge.
        width: '100%',
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        paddingHorizontal: compact ? 14 : 24,
        paddingVertical: 14,
        backgroundColor: COLORS.background,
        borderBottomWidth: hideBorder ? 0 : 1,
        borderBottomColor: COLORS.border,
        // Subtle elevation on web for hierarchy; no shadow on native to
        // avoid double-rendering against the SafeArea.
        ...(Platform.OS === 'web'
          ? ({ position: 'sticky' as any, top: 0, zIndex: 50 } as any)
          : {}),
      }}
    >
      {/* Brand cluster — logo + name */}
      <TouchableOpacity
        onPress={() => {
          const target = logoTargetFor(auth?.phase);
          if ('inApp' in target) {
            router.navigate(target.inApp as any);
          } else {
            Linking.openURL(target.external);
          }
        }}
        accessibilityRole="link"
        accessibilityLabel="Mentor Coaching AI"
        style={{ flexDirection: 'row', alignItems: 'center' }}
      >
        <Image
          source={LOGO_SRC}
          style={{ width: 32, height: 32, borderRadius: 7, marginRight: 10 }}
          resizeMode="contain"
        />
        {!compact ? <Text
          style={{
            color: COLORS.text.primary,
            fontWeight: '600',
            fontSize: 16,
            // Hide the wordmark on very narrow screens; the icon stays.
            ...(Platform.OS === 'web' ? ({ whiteSpace: 'nowrap' as any } as any) : {}),
          }}
          numberOfLines={1}
        >
          Mentor Coaching AI
        </Text> : null}
      </TouchableOpacity>

      {/* Right slot — defaults to language + Help */}
      {rightSlot ?? (
        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 12 }}>
          <LanguageSelector />
          {!veryCompact ? <TouchableOpacity
            onPress={() => Linking.openURL(helpUrlFor(language))}
            accessibilityRole="link"
            style={{ paddingHorizontal: 8, paddingVertical: 6 }}
          >
            <Text style={{ color: COLORS.text.secondary, fontSize: 14 }}>
              {t('auth.passwordless.chrome.help')}
            </Text>
          </TouchableOpacity> : null}
        </View>
      )}
    </View>
  );
}
